import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { enqueueSnackbar } from 'notistack';
import { type Ref, useImperativeHandle } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { IconSchool, IconCalendar } from '@tabler/icons-react';

import Input from '@/components/Input/Input';
import Select from '@/components/Select';
import { CURRENCY_OPTIONS } from '@/constants/constant';
import { ECurrency, ERoleAccess } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import {
  scheduleRenewalSchema,
  TScheduleRenewalFormValues,
  SUBSCRIPTION_INPUT_MAX_LENGTH,
} from '@/schema/subscriptionSchema';
import { scheduleSchoolRenewal } from '@/service/schoolService';
import { IScheduleRenewalPayload } from '@/types/payload';
import { getTodayIso, getNextDayIso, getPrevDayIso } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';

import DatePicker from '../../../components/DatePicker';

export interface IScheduleRenewalModalHandle {
  submit: () => Promise<boolean>;
}

dayjs.extend(utc);

interface IScheduleRenewalModalProps {
  schoolId?: string;
  schoolName?: string;
  expiryDate?: string;
  expiryDateIso?: string;
  ref?: Ref<IScheduleRenewalModalHandle>;
  onSubmitSuccess?: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

export const ScheduleRenewalModal = ({
  schoolId,
  schoolName,
  expiryDate,
  expiryDateIso,
  ref,
  onSubmitSuccess,
  onLoadingChange,
}: IScheduleRenewalModalProps) => {
  const { hasAccess } = useAccess();
  const { formatText } = useFormatText();
  const {
    control,
    register,
    trigger,
    getValues,
    watch,
    formState: { errors },
  } = useForm<TScheduleRenewalFormValues>({
    mode: 'onChange',
    resolver: zodResolver(scheduleRenewalSchema),
    defaultValues: {
      currency: ECurrency.KWD,
      subscriptionStartDate: '',
      subscriptionEndDate: '',
      transactionId: '',
    },
  });

  const subscriptionStartDate = watch('subscriptionStartDate');
  const subscriptionEndDate = watch('subscriptionEndDate');
  const todayIso = getTodayIso();
  const expiryNextDay = expiryDateIso ? getNextDayIso(expiryDateIso) : null;
  const minStartDate =
    expiryNextDay && expiryNextDay > todayIso ? expiryNextDay : todayIso;
  const minEndDate = subscriptionStartDate
    ? getNextDayIso(subscriptionStartDate)
    : minStartDate;
  const maxStartDate = subscriptionEndDate
    ? getPrevDayIso(subscriptionEndDate)
    : undefined;

  const licenseStats = [
    {
      label: formatText('school.schedule-renewal.school'),
      value: schoolName ?? '-',
      icon: IconSchool,
    },
    {
      label: formatText('school.schedule-renewal.expiry-date'),
      value: expiryDate ?? '-',
      icon: IconCalendar,
    },
  ];

  const onValidSubmit = async (
    data: TScheduleRenewalFormValues
  ): Promise<boolean> => {
    if (!schoolId) return false;

    onLoadingChange?.(true);
    try {
      const payload: IScheduleRenewalPayload = {
        school_id: schoolId,
        student_limit: data.studentLimit,
        currency: data.currency,
        amount: data.amount,
        start_date: data.subscriptionStartDate,
        end_date: data.subscriptionEndDate,
        transaction_id: data.transactionId,
      };
      const response = await scheduleSchoolRenewal(payload);
      if (response?.status) {
        enqueueSnackbar(
          response?.message ??
            formatText('school.schedule-renewal.success-message'),
          { variant: 'success' }
        );
        onSubmitSuccess?.();
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      onLoadingChange?.(false);
    }
  };

  const submit = async (): Promise<boolean> => {
    const isValid = await trigger();
    if (!isValid) return false;
    return onValidSubmit(getValues());
  };

  useImperativeHandle(ref, () => ({ submit }));

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  return (
    <div className="w-full bg-white space-y-10">
      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-neutral-primary">
          {formatText('school.schedule-renewal.license-details')}
        </h3>

        <div className="grid grid-cols-2 gap-y-6 gap-x-12">
          {licenseStats.map(({ label, value, icon: Icon }) => (
            <div key={label} className="flex items-start gap-3">
              <div className="mt-0.5 text-neutral-hover">
                <Icon size={20} />
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-sm leading-5 text-neutral-secondary">
                  {label}
                </span>

                <span className="text-base font-medium text-neutral-primary">
                  {value}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-neutral-primary leading-7">
          {formatText('school.schedule-renewal.renewal-configuration')}
        </h3>
        <div className="flex flex-col gap-4">
          <Input
            label={formatText('school.schedule-renewal.student-seats')}
            name="studentLimit"
            type="number"
            placeholder={formatText(
              'school.schedule-renewal.student-seats-placeholder'
            )}
            min={1}
            maxlength={SUBSCRIPTION_INPUT_MAX_LENGTH}
            integer
            register={register}
            error={
              errors.studentLimit?.message
                ? formatText(errors.studentLimit.message)
                : undefined
            }
            isRequired
          />

          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <Input
              label={formatText('school.schedule-renewal.amount')}
              name="amount"
              type="number"
              step="0.01"
              placeholder={formatText(
                'school.schedule-renewal.amount-placeholder'
              )}
              min={1}
              maxlength={SUBSCRIPTION_INPUT_MAX_LENGTH}
              register={register}
              error={
                errors.amount?.message
                  ? formatText(errors.amount.message)
                  : undefined
              }
              isRequired
              className="w-full sm:flex-1"
            />
            <Controller
              name="currency"
              control={control}
              render={({ field }) => (
                <Select
                  name={formatText('school.schedule-renewal.currency')}
                  placeholder={formatText(
                    'school.schedule-renewal.currency-placeholder'
                  )}
                  options={CURRENCY_OPTIONS}
                  selected={field.value ?? ''}
                  onChange={field.onChange}
                  error={
                    errors.currency?.message
                      ? formatText(errors.currency.message)
                      : undefined
                  }
                  isRequired
                  mainClassName="w-full sm:flex-1"
                />
              )}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Controller
              name="subscriptionStartDate"
              control={control}
              render={({ field }) => (
                <div className="flex-1">
                  <DatePicker
                    label={formatText(
                      'school.schedule-renewal.subscription-start-date'
                    )}
                    placeholder={formatText(
                      'school.schedule-renewal.subscription-start-date-placeholder'
                    )}
                    value={field.value || null}
                    onChange={field.onChange}
                    min={minStartDate}
                    max={maxStartDate}
                    error={
                      errors.subscriptionStartDate?.message
                        ? formatText(errors.subscriptionStartDate.message)
                        : undefined
                    }
                    isRequired
                  />
                </div>
              )}
            />

            <Controller
              name="subscriptionEndDate"
              control={control}
              render={({ field }) => (
                <div className="flex-1">
                  <DatePicker
                    label={formatText(
                      'school.schedule-renewal.subscription-end-date'
                    )}
                    placeholder={formatText(
                      'school.schedule-renewal.subscription-end-date-placeholder'
                    )}
                    value={field.value || null}
                    onChange={field.onChange}
                    min={minEndDate}
                    error={
                      errors.subscriptionEndDate?.message
                        ? formatText(errors.subscriptionEndDate.message)
                        : undefined
                    }
                    isRequired
                  />
                </div>
              )}
            />
          </div>

          <Input
            label={formatText('school.schedule-renewal.transaction-id')}
            name="transactionId"
            placeholder={formatText(
              'school.schedule-renewal.transaction-id-placeholder'
            )}
            register={register}
            error={
              errors.transactionId?.message
                ? formatText(errors.transactionId.message)
                : undefined
            }
            isRequired
          />
        </div>
      </div>
    </div>
  );
};
